import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Moon, Music, Star, ChevronRight, Clock, MapPin, Users, Shield, CheckCircle, Camera, Heart, ChevronDown, ChevronUp, Leaf, Coffee, Building, Download } from 'lucide-react';
import { motion } from 'framer-motion';

const NightCrawlingSection = () => {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const nightlifePackages = [
    {
      id: 1,
      title: 'Kampala Night Crawling',
      image: 'https://images.unsplash.com/photo-1566737236500-c8ac43014a67?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80',
      price: 30,
      highlights: ['VIP club entry', 'Street food tasting', 'Expert guides']
    },
    {
      id: 2,
      title: 'Premium VIP Experience',
      image: 'https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1829&q=80',
      price: 100,
      highlights: ['Bottle service', 'Private transport', 'Professional photos']
    },
    {
      id: 3,
      title: 'Jinja Night Crawling',
      image: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80',
      price: 30,
      highlights: ['Riverside bars', 'Live music', 'Night boat cruise']
    }
  ];

  return (
    <section className="py-12 bg-gradient-to-b from-gray-900 to-purple-900 text-white relative overflow-hidden">
      {/* Animated party effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-purple-500 rounded-full animate-ping" style={{ animationDuration: '3s', animationDelay: '0.5s' }}></div>
        <div className="absolute top-1/3 left-1/2 w-2 h-2 bg-blue-500 rounded-full animate-ping" style={{ animationDuration: '2.5s', animationDelay: '0.2s' }}></div>
        <div className="absolute top-1/2 left-1/3 w-2 h-2 bg-pink-500 rounded-full animate-ping" style={{ animationDuration: '4s', animationDelay: '1s' }}></div>
        <div className="absolute top-2/3 left-2/3 w-2 h-2 bg-green-500 rounded-full animate-ping" style={{ animationDuration: '3.5s', animationDelay: '0.7s' }}></div>
        <div className="absolute top-3/4 left-1/4 w-2 h-2 bg-yellow-500 rounded-full animate-ping" style={{ animationDuration: '2.8s', animationDelay: '1.2s' }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <div className="inline-flex items-center bg-purple-500/20 rounded-full px-2.5 py-0.5 text-xs text-purple-400 mb-4 backdrop-blur-sm">
              <Moon className="h-3 w-3 mr-1" />
              NEW EXPERIENCE
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Uganda Night Crawling & Club Guide</h2>
            <p className="text-white/80 max-w-2xl">
              Experience Kampala after dark with our exclusive nightlife packages. VIP club access, street food, and expert local guides for an unforgettable night out!
            </p>
          </div>
          <Link 
            to="/Uganda_Nightlife_Ultimate_Guide"
            className="hidden md:flex items-center text-purple-400 hover:text-purple-300 mt-4 md:mt-0 group"
          >
            View All Packages
            <ChevronRight className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-3 gap-3 md:gap-6">
          {nightlifePackages.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden border border-white/20 hover:border-purple-500/50 transition-all duration-300 transform hover:-translate-y-1"
              onMouseEnter={() => setHoveredCard(pkg.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="relative h-28 md:h-36">
                <img 
                  src={pkg.image}
                  alt={pkg.title}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                <div className="absolute bottom-2 left-2">
                  <h3 className="text-sm md:text-base font-bold text-white line-clamp-1">{pkg.title}</h3>
                  <div className="flex items-center mt-0.5">
                    <Star className="h-3 w-3 text-yellow-400 fill-current" />
                    <span className="ml-1 text-white/80 text-xs">4.9</span>
                    <span className="mx-1 text-white/60">•</span>
                    <span className="text-white/80 text-xs">From ${pkg.price}</span>
                  </div>
                </div>
              </div>
              
              <div className="p-2 md:p-3">
                <ul className="space-y-1 mb-2 md:mb-3">
                  {pkg.highlights.map((highlight, idx) => (
                    <li key={idx} className="flex items-start text-xs text-white/80">
                      <CheckCircle className="h-3 w-3 text-purple-400 mr-1 flex-shrink-0 mt-0.5" />
                      <span className="line-clamp-1">{highlight}</span>
                    </li>
                  ))}
                </ul>
                
                <Link
                  to="/Uganda_Nightlife_Ultimate_Guide"
                  className="w-full py-1 md:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center text-xs"
                >
                  <Moon className="h-3 w-3 mr-1" />
                  Book Now
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 text-center md:hidden">
          <Link 
            to="/Uganda_Nightlife_Ultimate_Guide"
            className="inline-flex items-center text-purple-400 hover:text-purple-300 group"
          >
            View All Packages
            <ChevronRight className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="mt-8 md:mt-12 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 flex items-center">
                <Shield className="h-4 w-4 md:h-5 md:w-5 text-purple-400 mr-2" />
                Why Book With Us
              </h3>
              <ul className="space-y-2 md:space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80 text-xs md:text-sm">VIP access to exclusive clubs with no waiting in lines</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80 text-xs md:text-sm">Expert local guides who know the best spots and hidden gems</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80 text-xs md:text-sm">All-inclusive packages with no hidden fees</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-purple-900/50 p-4 md:p-6 rounded-xl">
              <div className="flex items-center mb-3 md:mb-4">
                <Camera className="h-5 w-5 md:h-6 md:w-6 text-purple-400 mr-2" />
                <h3 className="text-lg md:text-xl font-bold">Photo Package Add-on</h3>
              </div>
              <p className="text-white/80 mb-3 md:mb-4 text-xs md:text-sm">
                Capture your night with our professional photography add-on. A dedicated photographer will join your group to document your experience.
              </p>
              <div className="flex items-center justify-between">
                <span className="text-purple-400 font-semibold text-xs md:text-sm">$30 per group</span>
                <Link 
                  to="/Uganda_Nightlife_Ultimate_Guide"
                  className="px-3 py-1 md:px-4 md:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-xs md:text-sm"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NightCrawlingSection;